import React, { useRef, useEffect, useState } from 'react';

interface RevealProps {
  children: React.ReactNode;
  animation?: 'fade-in' | 'slide-up' | 'slide-left' | 'slide-right' | 'scale';
  delay?: number;
  duration?: number;
  threshold?: number;
  className?: string;
  once?: boolean;
}

const Reveal: React.FC<RevealProps> = ({
  children,
  animation = 'slide-up',
  delay = 0,
  duration = 500,
  threshold = 0.1,
  className = '',
  once = true,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once && element) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [once, threshold]);

  const animationStyles: React.CSSProperties = {
    opacity: isVisible ? 1 : 0,
    transform: getTransformValue(animation, isVisible),
    transition: `all ${duration}ms ease-out ${delay}ms`,
  };

  return (
    <div ref={ref} style={animationStyles} className={className}>
      {children}
    </div>
  );
};

const getTransformValue = (animation: string, isVisible: boolean): string => {
  if (!isVisible) {
    switch (animation) {
      case 'fade-in':
        return 'none';
      case 'slide-up':
        return 'translateY(50px)';
      case 'slide-left':
        return 'translateX(-50px)';
      case 'slide-right':
        return 'translateX(50px)';
      case 'scale':
        return 'scale(0.9)';
      default:
        return 'translateY(50px)';
    }
  }
  return 'translate(0) scale(1)';
};

export default Reveal;

